/**
 * Verantwortlichkeit (PSIRT) — who answers for each product under the CRA.
 *
 * Two layers, because they change at different speeds and are owned by
 * different people:
 *
 *   PSIRT-Teams   organisational, shared by many products: Meldestelle,
 *                 CVD-Policy, the Art. 14 reporting channel to CSIRT/ENISA.
 *   Produkte      one responsibility per product: team, Product Security
 *                 Owner, CRA category, support period, update channel.
 *
 * The verdict per product (and per team) comes from
 * app/services/psirt_service.py and is the same one the CRA-Check shows as its
 * "Verantwortlichkeit (PSIRT)" rule. Writes go through the generic Stammdaten
 * CRUD (`cmdb.write`); an auditor sees everything read-only.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../components/Banner';
import Chip from '../components/Chip';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchPsirtOverview, provisionCraWatchSchema } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DASH, countLabel, fmtDate } from '../lib/format';
import type {
  KpiCounter,
  PsirtCheck,
  PsirtOverviewResponse,
  PsirtProductRow,
  PsirtState,
  PsirtTeam,
} from '../lib/types';

import AssignTeamModal from './psirt/AssignTeamModal';
import ProductFormModal from './psirt/ProductFormModal';
import TeamFormModal from './psirt/TeamFormModal';
import {
  KATEGORIE_SHORT,
  KATEGORIE_TONE,
  PSIRT_STATE_LABELS,
  PSIRT_STATE_RANK,
  PSIRT_STATE_TONE,
  fmtDayIso,
  fmtSpan,
  stateName,
} from './psirt/psirtModel';

import './psirt/psirt.css';

type Filter = 'alle' | PsirtState | 'ohne-team' | 'support' | 'pruefung';

const FILTERS: readonly { key: Filter; label: string }[] = [
  { key: 'alle', label: 'Alle Produkte' },
  { key: 'fehler', label: 'Lücken' },
  { key: 'warnung', label: 'Hinweise' },
  { key: 'ohne-team', label: 'Ohne Team' },
  { key: 'ok', label: 'Vollständig' },
  { key: 'support', label: 'Support endet ≤ 12 Mon.' },
  { key: 'pruefung', label: 'Prüfung fällig' },
];

/** Support ends within the warning window or has already ended (same rule as
 *  the summary: support_bald + support_abgelaufen). */
function supportEndingSoon(row: PsirtProductRow, warnMonths: number): boolean {
  if (!row.support_ende || row.support_restmonate === null) return false;
  const expired = row.support_ende < new Date().toISOString().slice(0, 10);
  return expired || row.support_restmonate < warnMonths;
}

/** Review of the responsibility record is due (same rule as the summary). */
function reviewDue(row: PsirtProductRow): boolean {
  return row.verantwortung_id !== null && row.checks.some((check) => check.key === 'review' && check.state !== 'ok');
}

function matchesFilter(row: PsirtProductRow, filter: Filter, warnMonths: number): boolean {
  if (filter === 'alle') return true;
  if (filter === 'ohne-team') return row.team_id === null;
  if (filter === 'support') return supportEndingSoon(row, warnMonths);
  if (filter === 'pruefung') return reviewDue(row);
  return row.state === filter;
}

type Dialog =
  | { kind: 'team'; team: PsirtTeam | null; returnTo?: PsirtProductRow }
  | { kind: 'product'; row: PsirtProductRow }
  | { kind: 'assign' };

function CheckItems({ checks }: { checks: readonly PsirtCheck[] }): ReactNode {
  return (
    <ul className="psirt-checks">
      {checks.map((check) => (
        <li key={check.key} data-state={check.state}>
          <span className="psirt-dot" aria-hidden="true" />
          <div>
            <div className="psirt-check-label">
              {check.label}
              <span className="visually-hidden">, {PSIRT_STATE_LABELS[check.state]}</span>
            </div>
            <div className="psirt-check-detail">{check.detail}</div>
            {check.hint && check.state !== 'ok' ? <div className="psirt-check-hint">{check.hint}</div> : null}
          </div>
          <span className="psirt-check-ref">{check.reference}</span>
        </li>
      ))}
    </ul>
  );
}

function Progress({ done, total, state }: { done: number; total: number; state: PsirtState }): ReactNode {
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="psirt-progress" title={`${done} von ${total} Anforderungen erfüllt`}>
      <span className="psirt-meter" data-state={state}>
        <span style={{ width: `${percent}%` }} />
      </span>
      <span className="psirt-progress-text">
        {done}/{total}
      </span>
    </div>
  );
}

function TeamCard({
  team,
  canWrite,
  onEdit,
}: {
  team: PsirtTeam;
  canWrite: boolean;
  onEdit: () => void;
}): ReactNode {
  const v = team.values;
  const open = team.checks.filter((check) => check.state !== 'ok');
  return (
    <article className="psirt-team" data-state={team.state}>
      <header>
        <div className="psirt-team-title">
          <div>
            <h3>{team.label}</h3>
            <span className="muted">
              {team.produkte === 0
                ? 'noch keinem Produkt zugeordnet'
                : `${team.produkte} Produkt${team.produkte === 1 ? '' : 'e'}`}
            </span>
          </div>
        </div>
        <Chip tone={PSIRT_STATE_TONE[team.state]}>{PSIRT_STATE_LABELS[team.state]}</Chip>
      </header>

      <dl className="psirt-team-facts">
        <div>
          <dt>Meldestelle</dt>
          <dd>{v.MeldeEmail ? <a href={`mailto:${v.MeldeEmail}`}>{v.MeldeEmail}</a> : <em>fehlt</em>}</dd>
        </div>
        <div>
          <dt>Erreichbarkeit</dt>
          <dd>
            {v.Erreichbarkeit || <em>nicht angegeben</em>}
            {v.Telefon ? <span className="muted"> · {v.Telefon}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Koordinierendes CSIRT</dt>
          <dd>
            {v.CsirtKoordinator || <em>fehlt</em>}
            {v.Mitgliedstaat ? <span className="muted"> · {stateName(v.Mitgliedstaat)}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Meldet an ENISA</dt>
          <dd>{v.MelderName || v.MelderEmail || <em>fehlt</em>}</dd>
        </div>
        <div>
          <dt>Leitung</dt>
          <dd>
            {v.LeitungName || <em>nicht benannt</em>}
            {v.VertretungName ? <span className="muted"> · Vertretung {v.VertretungName}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Offenlegung</dt>
          <dd className="psirt-links">
            {v.CvdPolicyUrl ? (
              <a href={v.CvdPolicyUrl} target="_blank" rel="noreferrer noopener">
                CVD-Policy <Icon name="external" size={12} />
              </a>
            ) : (
              <em>CVD-Policy fehlt</em>
            )}
            {v.SecurityTxtUrl ? (
              <a href={v.SecurityTxtUrl} target="_blank" rel="noreferrer noopener">
                security.txt <Icon name="external" size={12} />
              </a>
            ) : null}
          </dd>
        </div>
      </dl>

      <footer>
        {open.length > 0 ? (
          <span className="psirt-team-open">Offen: {open.map((check) => check.label).join(', ')}</span>
        ) : (
          <span className="psirt-team-open ok">Alle {team.gesamt} Team-Anforderungen erfüllt</span>
        )}
        <button type="button" className="small" onClick={onEdit}>
          {canWrite ? 'Bearbeiten' : 'Details'}
        </button>
      </footer>
    </article>
  );
}

export function VerantwortlichkeitPage(): ReactNode {
  const { has } = useAuth();
  const toast = useToast();
  const canWrite = has('cmdb.write');
  const canProvision = has('schema.admin');

  const [data, setData] = useState<PsirtOverviewResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [filter, setFilter] = useState<Filter>('alle');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const load = useCallback(async (mode: 'initial' | 'refresh'): Promise<PsirtOverviewResponse | null> => {
    if (mode === 'initial') setLoading(true);
    else setRefreshing(true);
    try {
      const response = await fetchPsirtOverview();
      setData(response);
      setLoadError(null);
      return response;
    } catch (error) {
      setLoadError(errorMessage(error));
      return null;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load('initial');
  }, [load]);

  const onSaved = useCallback((): void => {
    setDialog(null);
    void load('refresh');
  }, [load]);

  /** A team created from inside the product dialog returns to that product. */
  const onTeamSaved = useCallback(
    async (returnTo: PsirtProductRow | undefined): Promise<void> => {
      setDialog(null);
      const response = await load('refresh');
      if (!returnTo || !response) return;
      const row = response.rows.find((entry) => entry.produkt_id === returnTo.produkt_id);
      if (row) setDialog({ kind: 'product', row });
    },
    [load],
  );

  const provision = useCallback(async (): Promise<void> => {
    setProvisioning(true);
    try {
      const response = await provisionCraWatchSchema();
      if (response.status === 'partial') {
        toast.error(`Datenmodell teilweise ergänzt: ${response.result.errors.join('; ')}`);
      } else {
        toast.success('Datenmodell für Verantwortlichkeiten ergänzt.');
      }
      await load('refresh');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setProvisioning(false);
    }
  }, [load, toast]);

  const rows = useMemo((): PsirtProductRow[] => {
    const all = data?.rows ?? [];
    const term = search.trim().toLowerCase();
    return all.filter((row) => {
      if (!matchesFilter(row, filter, data?.thresholds.support_warn_months ?? 12)) return false;
      if (term === '') return true;
      return [row.produkt, row.modellreihe, row.team, row.values?.OwnerName, row.values?.OwnerEmail]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(term));
    });
  }, [data, filter, search]);

  const filterCounts = useMemo((): Record<Filter, number> => {
    const all = data?.rows ?? [];
    return {
      alle: all.length,
      fehler: all.filter((row) => row.state === 'fehler').length,
      warnung: all.filter((row) => row.state === 'warnung').length,
      ok: all.filter((row) => row.state === 'ok').length,
      'ohne-team': all.filter((row) => row.team_id === null).length,
      support: all.filter((row) => supportEndingSoon(row, data?.thresholds.support_warn_months ?? 12)).length,
      pruefung: all.filter(reviewDue).length,
    };
  }, [data]);

  const counters = useMemo((): KpiCounter[] => {
    const summary = data?.summary;
    if (!summary) return [];
    return [
      { label: 'Produkte', value: summary.produkte, tone: 'neutral' },
      {
        label: 'Vollständig geregelt',
        value: summary.vollstaendig,
        tone: summary.produkte > 0 && summary.vollstaendig === summary.produkte ? 'good' : 'neutral',
      },
      {
        label: 'Mit Lücken',
        value: summary.luecken,
        tone: summary.luecken > 0 ? 'alert' : 'good',
        title: 'Mindestens eine CRA-Pflichtangabe fehlt.',
      },
      {
        label: 'Ohne PSIRT-Team',
        value: summary.ohne_zustaendigkeit,
        tone: summary.ohne_zustaendigkeit > 0 ? 'alert' : 'good',
      },
      {
        label: 'Support endet ≤ 12 Mon.',
        value: summary.support_bald + summary.support_abgelaufen,
        tone: summary.support_bald + summary.support_abgelaufen > 0 ? 'warn' : 'good',
        title: `${summary.support_bald} endet in den nächsten 12 Monaten, ${summary.support_abgelaufen} bereits abgelaufen.`,
      },
      {
        label: 'Prüfung fällig',
        value: summary.pruefung_faellig,
        tone: summary.pruefung_faellig > 0 ? 'warn' : 'good',
        title: 'Noch nie oder vor mehr als 12 Monaten geprüft.',
      },
    ];
  }, [data]);

  // Every tile is an option of the status select below; tile and select show
  // the same filter, a second click on the tile goes back to „Alle“.
  const kpiTiles = useMemo((): KpiItem[] => {
    const targets: Readonly<Record<string, Filter>> = {
      Produkte: 'alle',
      'Vollständig geregelt': 'ok',
      'Mit Lücken': 'fehler',
      'Ohne PSIRT-Team': 'ohne-team',
      'Support endet ≤ 12 Mon.': 'support',
      'Prüfung fällig': 'pruefung',
    };
    return counters.map((counter) => {
      const target = targets[counter.label];
      return target
        ? {
            ...counter,
            ariaLabel: `${counter.label}: ${counter.value} anzeigen`,
            selected: filter === target && search.trim() === '',
            controls: 'psirt-results',
            onClick: () => {
              const active = filter === target && search.trim() === '';
              setFilter(active ? 'alle' : target);
              setSearch('');
            },
          }
        : counter;
    });
  }, [counters, filter, search]);

  const columns = useMemo(
    (): readonly DataTableColumn<PsirtProductRow>[] => [
      {
        key: 'state',
        header: 'Status',
        width: '118px',
        sortValue: (row) => PSIRT_STATE_RANK[row.state] * 100 + row.erfuellt,
        render: (row) => <Chip tone={PSIRT_STATE_TONE[row.state]}>{PSIRT_STATE_LABELS[row.state]}</Chip>,
      },
      {
        key: 'produkt',
        header: 'Produkt',
        sortValue: (row) => row.produkt.toLowerCase(),
        render: (row) => (
          <>
            <strong>{row.produkt}</strong>
            <div className="muted">
              {[
                row.modellreihe,
                row.instanzen > 0 ? `${row.instanzen} ausgeliefert` : 'nicht ausgeliefert',
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </>
        ),
      },
      {
        key: 'team',
        header: 'PSIRT-Team',
        sortValue: (row) => row.team.toLowerCase() || '~',
        render: (row) => (row.team ? row.team : <span className="psirt-missing">nicht zugeordnet</span>),
      },
      {
        key: 'owner',
        header: 'Security Owner',
        sortValue: (row) => (row.values?.OwnerName ?? '~').toLowerCase(),
        render: (row) =>
          row.values?.OwnerName ? (
            <>
              <div>{row.values.OwnerName}</div>
              {row.values.OwnerEmail ? (
                <div className="muted psirt-mail" title={row.values.OwnerEmail}>
                  {row.values.OwnerEmail}
                </div>
              ) : null}
            </>
          ) : (
            <span className="psirt-missing">nicht benannt</span>
          ),
      },
      {
        key: 'kategorie',
        header: 'Kategorie',
        width: '118px',
        sortValue: (row) => row.kategorie ?? '~',
        render: (row) =>
          row.kategorie ? (
            <Chip tone={KATEGORIE_TONE[row.kategorie]}>{KATEGORIE_SHORT[row.kategorie]}</Chip>
          ) : (
            <span className="muted">{DASH}</span>
          ),
      },
      {
        key: 'support',
        header: 'Support bis',
        width: '132px',
        sortValue: (row) => row.support_ende ?? '9999',
        render: (row) => {
          if (!row.support_ende || row.support_restmonate === null) {
            return <span className="psirt-missing">offen</span>;
          }
          const expired = row.support_ende < new Date().toISOString().slice(0, 10);
          const soon = !expired && row.support_restmonate < (data?.thresholds.support_warn_months ?? 12);
          return (
            <>
              <div>{fmtDayIso(row.support_ende)}</div>
              <div className={expired ? 'psirt-support-cell alert' : soon ? 'psirt-support-cell warn' : 'muted'}>
                {expired ? 'abgelaufen' : `noch ${fmtSpan(row.support_restmonate)}`}
              </div>
            </>
          );
        },
      },
      {
        key: 'progress',
        header: 'CRA-Angaben',
        width: '132px',
        sortValue: (row) => row.erfuellt / Math.max(row.gesamt, 1),
        render: (row) => <Progress done={row.erfuellt} total={row.gesamt} state={row.state} />,
      },
      {
        key: 'action',
        header: <span className="visually-hidden">Aktion</span>,
        width: '120px',
        align: 'right',
        render: (row) => (
          <button
            type="button"
            className={`small${canWrite && row.verantwortung_id === null ? ' primary' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              setDialog({ kind: 'product', row });
            }}
          >
            {!canWrite ? 'Details' : row.verantwortung_id === null ? 'Festlegen' : 'Bearbeiten'}
          </button>
        ),
      },
    ],
    [canWrite, data?.thresholds.support_warn_months],
  );

  const renderDetail = useCallback(
    (row: PsirtProductRow): ReactNode => (
      <div className="psirt-detail">
        <div>
          <h3>Organisation {row.team ? <span className="muted">· über {row.team}</span> : null}</h3>
          <CheckItems checks={row.checks.filter((check) => check.group === 'organisation')} />
        </div>
        <div>
          <h3>Produkt</h3>
          <CheckItems checks={row.checks.filter((check) => check.group === 'produkt')} />
        </div>
      </div>
    ),
    [],
  );

  if (loading) return <PageSpinner text="Lädt Verantwortlichkeiten…" />;

  const teams = data?.teams ?? [];
  const schemaReady = data?.schema_ready ?? false;

  return (
    <>
      <PageHeader
        title="Verantwortlichkeit (PSIRT)"
        sub={
          <>
            Meldepflichten nach Art. 14 CRA gelten seit dem 11.{'\u00a0'}September{'\u00a0'}2026
            {data?.generated_at ? ` · Stand ${fmtDate(data.generated_at)}` : null}
            {' · '}Je Instanz im <Link to="/cra-check">CRA-Check</Link>
          </>
        }
        actions={
          <>
            {refreshing ? <Spinner label="Aktualisiert…" /> : null}
            <button type="button" onClick={() => void load('refresh')} disabled={refreshing}>
              Neu laden
            </button>
            {canWrite && schemaReady ? (
              <button type="button" className="primary" onClick={() => setDialog({ kind: 'team', team: null })}>
                PSIRT-Team anlegen
              </button>
            ) : null}
          </>
        }
      />

      {loadError ? (
        <Banner kind="error" sub="Prüfen Sie die Verbindung zu CMDBuild.">
          {loadError}
        </Banner>
      ) : null}

      {data && !schemaReady ? (
        <div className="card psirt-setup">
          <div className="card-body">
            <h2>Datenmodell ergänzen</h2>
            <p>
              Für Verantwortlichkeiten fehlen in CMDBuild noch Teile des Datenmodells. Bestehende Daten
              bleiben beim Ergänzen unverändert.
            </p>
            {canProvision ? (
              <button type="button" className="primary" onClick={() => void provision()} disabled={provisioning}>
                {provisioning ? <Spinner variant="on-accent" label="Ergänzt Datenmodell…" /> : 'Datenmodell ergänzen'}
              </button>
            ) : (
              <p className="muted">Das kann nur ein Administrator mit Schema-Berechtigung.</p>
            )}
          </div>
        </div>
      ) : null}

      {data && schemaReady ? (
        <>
          <KpiRow counters={kpiTiles} />

          <section className="card">
            <div className="card-head">
              <h2>PSIRT-Teams</h2>
              <span className="muted">Meldestelle, CVD-Policy und Meldeweg gelten für alle zugeordneten Produkte.</span>
            </div>
            <div className="card-body">
              {teams.length === 0 ? (
                <EmptyState
                  title="Noch kein PSIRT-Team angelegt"
                  action={
                    canWrite ? (
                      <button type="button" className="primary" onClick={() => setDialog({ kind: 'team', team: null })}>
                        Erstes PSIRT-Team anlegen
                      </button>
                    ) : undefined
                  }
                >
                  Dafür brauchen Sie eine öffentliche Melde-Adresse, den Link zur CVD-Policy und den
                  Meldeweg an das koordinierende CSIRT.
                </EmptyState>
              ) : (
                <div className="psirt-teams">
                  {teams.map((team) => (
                    <TeamCard
                      key={team.id}
                      team={team}
                      canWrite={canWrite}
                      onEdit={() => setDialog({ kind: 'team', team })}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section id="psirt-results" className="card">
            <div className="card-head table-head">
              <h2>
                Verantwortlichkeit je Produkt
              </h2>
              {canWrite && teams.length > 0 && (data.rows.length ?? 0) > 0 ? (
                <button type="button" onClick={() => setDialog({ kind: 'assign' })}>
                  Team zuweisen…
                </button>
              ) : null}
            </div>
            <div className="table-toolbar">
              <input
                type="search"
                value={search}
                placeholder="Produkt, Team, Person…"
                aria-label="Verantwortlichkeiten durchsuchen"
                onChange={(event) => setSearch(event.target.value)}
              />
              <select
                value={filter}
                onChange={(event) => setFilter(event.target.value as Filter)}
                aria-label="Nach Status filtern"
              >
                {FILTERS.map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {entry.label} ({filterCounts[entry.key]})
                  </option>
                ))}
              </select>
              <div className="table-toolbar-options">
                {filter !== 'alle' || search.trim() !== '' ? (
                  <button type="button" className="link" onClick={() => { setFilter('alle'); setSearch(''); }}>
                    Filter zurücksetzen
                  </button>
                ) : null}
                <span className="table-count" aria-live="polite">
                  {rows.length === data.rows.length
                    ? countLabel(rows.length, 'Produkt', 'Produkte')
                    : `${rows.length} von ${countLabel(data.rows.length, 'Produkt', 'Produkte')}`}
                </span>
              </div>
            </div>
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.produkt_id}
              renderDetail={renderDetail}
              mobileBreakpoint={1080}
              pageSize={10}
              paginationResetKey={JSON.stringify([filter, search])}
              resetPageOnRowsChange={false}
              caption="Verantwortlichkeit je Produkt"
              emptyText={
                data.rows.length === 0
                  ? 'Noch keine Produkte im Katalog. Unter „Stammdaten“ anlegen oder per CSV importieren.'
                  : 'Kein Produkt passt zu den Filtern.'
              }
            />
          </section>
        </>
      ) : null}

      {dialog?.kind === 'team' ? (
        <TeamFormModal
          team={dialog.team}
          canWrite={canWrite}
          onClose={() =>
            setDialog(dialog.returnTo ? { kind: 'product', row: dialog.returnTo } : null)
          }
          onSaved={() => void onTeamSaved(dialog.returnTo)}
        />
      ) : null}
      {dialog?.kind === 'product' && data ? (
        <ProductFormModal
          row={dialog.row}
          teams={teams}
          kategorien={data.kategorien}
          kategorieFolgen={data.kategorie_folgen}
          thresholds={data.thresholds}
          canWrite={canWrite}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
          onCreateTeam={() => setDialog({ kind: 'team', team: null, returnTo: dialog.row })}
        />
      ) : null}
      {dialog?.kind === 'assign' && data ? (
        <AssignTeamModal rows={data.rows} teams={teams} onClose={() => setDialog(null)} onSaved={onSaved} />
      ) : null}
    </>
  );
}

export default VerantwortlichkeitPage;
